import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  forwardRef,
} from 'react';

import { Input } from 'antd';
import PropTypes from 'prop-types';

import styles from './styles.module.less';

/**
 * Search input for table column filters. Supports Ant Design Table filterDropdown and standalone mode.
 * Search triggers on Enter key or via the external triggerSearch ref. Exposes triggerSearch via ref.
 */
const ColumnSearchInput = forwardRef(
  (
    {
      value = '',
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      onSearch,
      placeholder,
      size = 'small',
      inputClassName,
      wrapperClassName,
      ...rest
    },
    ref,
  ) => {
    const isAntDesignMode = !!setSelectedKeys;
    const displayValue = isAntDesignMode ? (selectedKeys?.[0] ?? '') : value;

    const [localValue, setLocalValue] = useState(displayValue);
    const inputRef = useRef(null);

    /** Syncs local value when displayValue changes (e.g. from parent or filter reset). */
    useEffect(() => {
      setLocalValue(displayValue);
    }, [displayValue]);

    /** Focuses the input on mount for immediate typing. */
    useEffect(() => {
      inputRef.current?.focus();
    }, []);

    /** Calls onSearch with the value and updates Ant Design filter state (setSelectedKeys, confirm). */
    const applySearch = useCallback(
      searchValue => {
        onSearch?.(searchValue);
        if (isAntDesignMode) {
          setSelectedKeys(searchValue ? [searchValue] : []);
          confirm?.();
        }
      },
      [onSearch, isAntDesignMode, setSelectedKeys, confirm],
    );

    /** Applies search with current local value (trimmed). Exposed via ref for external trigger (e.g. search button). */
    const triggerSearch = useCallback(() => {
      const trimmed = localValue?.trim() || '';
      applySearch(trimmed);
    }, [localValue, applySearch]);

    useImperativeHandle(ref, () => ({ triggerSearch }), [triggerSearch]);

    /** Updates local state on input change. Does not trigger search (waits for Enter or button). */
    const handleChange = useCallback(
      e => {
        const value = e.target.value;
        setLocalValue(value);

        if (value === '' && e.type === 'click') {
          applySearch('');
        }
      },
      [applySearch],
    );

    /** Triggers search when user presses Enter. */
    const handlePressEnter = useCallback(() => {
      triggerSearch();
    }, [triggerSearch]);

    return (
      <div
        className={wrapperClassName ? `${styles.wrapper} ${wrapperClassName}` : styles.wrapper}
        {...rest}
      >
        <Input
          ref={inputRef}
          allowClear
          className={inputClassName ? `${styles.input} ${inputClassName}` : styles.input}
          placeholder={placeholder}
          value={localValue}
          onChange={handleChange}
          onPressEnter={handlePressEnter}
          size={size}
        />
      </div>
    );
  },
);

ColumnSearchInput.displayName = 'ColumnSearchInput';

ColumnSearchInput.propTypes = {
  value: PropTypes.string,
  setSelectedKeys: PropTypes.func,
  selectedKeys: PropTypes.array,
  confirm: PropTypes.func,
  clearFilters: PropTypes.func,
  onSearch: PropTypes.func,
  placeholder: PropTypes.string,
  size: PropTypes.oneOf(['small', 'middle', 'large']),
  inputClassName: PropTypes.string,
  wrapperClassName: PropTypes.string,
};

export default ColumnSearchInput;
